import { isFinished, type JobDetail } from "./jobApi";

export type PhaseState =
  | "done"
  | "current"
  | "awaiting_approval"
  | "waiting_for_person"
  | "supervisor_control"
  | "failed"
  | "stopped"
  | "not_reached"
  | "unrecorded";

export const PHASE_STATE_LABEL: Record<PhaseState, string> = {
  done: "Completed",
  current: "In progress",
  awaiting_approval: "Waiting for approval",
  waiting_for_person: "Waiting for a person",
  supervisor_control: "Supervisor in control",
  failed: "Failed",
  stopped: "Stopped here",
  not_reached: "Not reached",
  unrecorded: "No steps recorded",
};

/** What the phase the runner is in is doing, from the job's API status. */
function liveState(apiStatus: string): PhaseState {
  switch (apiStatus.trim().toLowerCase()) {
    case "waiting_for_human":
      return "waiting_for_person";
    case "paused_for_takeover":
      return "supervisor_control";
    case "awaiting_approval":
    case "paused_for_approval":
      return "awaiting_approval";
    default:
      return "current";
  }
}

/**
 * Per-phase state.
 *
 * Which phase the runner is in comes from `currentPhaseOrder`: the API
 * reads it from the row written when a phase starts, so it is right
 * even while that phase takes no steps (waiting for a person, at a
 * checkpoint, under a supervisor's control). Earlier phases read
 * Completed. What the current phase is doing comes from the job's API
 * status, so a job waiting in phase 2 reads "Waiting for a person" on
 * phase 2, not "In progress" on phase 1.
 *
 * Runs from before the phase-start row existed (and older API builds)
 * have no `currentPhaseOrder`; for those the current phase is inferred
 * from step rows as before: the last phase with steps, or a phase that
 * has a pending approval (a checkpoint comes before the phase's first
 * action, so it has no rows yet).
 *
 * When a started job has no rows at all (every smoke run), nothing can
 * be inferred and every phase reads "No steps recorded".
 */
export function phaseStates(job: JobDetail, awaitingPhaseId: string | null): PhaseState[] {
  const phases = job.phaseDetails;
  const finished = isFinished(job.status);

  let reached = -1;
  if (job.currentPhaseOrder !== null) {
    reached = phases.findIndex((p) => p.order === job.currentPhaseOrder);
  }
  if (reached === -1) {
    phases.forEach((p, i) => {
      if (p.stepCount > 0 || p.started) reached = i;
    });
  }
  const awaitingIndex = finished ? -1 : phases.findIndex((p) => p.id === awaitingPhaseId);
  // A checkpoint can hold the job before the next phase records its start.
  const current = Math.max(reached, awaitingIndex);

  return phases.map((p, i) => {
    if (p.failed) return "failed";
    if (job.status === "success") return "done";
    if (i === awaitingIndex) return "awaiting_approval";
    if (current === -1) return job.startedAt || finished ? "unrecorded" : "not_reached";
    if (i < current) return "done";
    if (i === current) return finished ? "stopped" : liveState(job.apiStatus);
    return "not_reached";
  });
}
